/* eslint-disable */
// Universal Global Search — portable source hook (Phase B). Assembles all seven
// entity groups independently of the active portal and gates portal/page results
// through the existing permission hooks. Items, developments, designers and brands
// are answered by the server per (debounced) query — globalSearchServer.js, app-shell
// 2026-09-14: the overlay no longer pulls every item or reads the App's full
// developments list. Lenses reuse the lazy lens query, gated by 'lens' read access.
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { usePortalPermissions, canAccessPortal, isMiAdmin } from '../../hooks/usePortalPermissions';
import { LENS_BANK_KEY, lensBankQueryFn } from '../../portals/data/lensBank/lensApi';
import { GS_LTD_KEY, gsLtdQueryFn, buildPortals, buildPages, buildLtd } from './globalSearchData';
import { useGlobalSearchServer } from './globalSearchServer';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { lensMatch } from '../../portals/data/lensBank/lensSearch';
import { normalizeSearchTerm, searchAccessReady, searchRequestState } from './globalSearchExtraData';

export function useGlobalSearchSources({ q, cap, fields, role, userId, enabled = true }) {
  const permissionQuery = usePortalPermissions();
  const perm = permissionQuery.data;
  const accountId = userId || perm?.user?.id || null;
  const permissionsReady = searchAccessReady(permissionQuery, accountId);
  const accessFor = (portal) => enabled && permissionsReady && canAccessPortal(perm, portal);
  const canCatalog = accessFor('catalog');
  const canDev = accessFor('developments');
  const canQc = accessFor('qc');
  const canSales = accessFor('sales');
  const canProjects = accessFor('projects');
  const canOrders = accessFor('orders');
  const canLens = accessFor('lens');
  const canAcetate = accessFor('acetate');
  const canMi = permissionsReady && isMiAdmin(perm, role);

  // The server answers the four data groups for the debounced query (Conv #57: the
  // input stays immediate; only the debounced value reaches a queryKey).
  const dq = useDebouncedValue(q || '', 250);
  const { term } = normalizeSearchTerm(dq);
  const requestState = searchRequestState(q, dq, enabled && permissionsReady);
  const queryEnabled = requestState.enabled;
  const server = useGlobalSearchServer({
    q: term, cap, fields, userId: accountId, enabled: queryEnabled,
    canCatalog, canDev, canOrders, canAcetate,
  });
  // Lenses — gated by read access (default-deny → no request, no group).
  const lensQuery = useQuery({
    queryKey: [...LENS_BANK_KEY, 'search', accountId || 'no-account'], queryFn: lensBankQueryFn,
    staleTime: 5 * 60 * 1000, retry: 0, enabled: queryEnabled && canLens,
  });
  // LTD (lens developments) — same 'lens' gate as Lenses; own cached key (portal-independent).
  const ltdQuery = useQuery({
    queryKey: [...GS_LTD_KEY, 'search', accountId || 'no-account'], queryFn: gsLtdQueryFn,
    staleTime: 5 * 60 * 1000, retry: 0, enabled: queryEnabled && canLens,
  });

  const access = useMemo(() => ({
    catalog: canCatalog, developments: canDev, qc: canQc, sales: canSales,
    projects: canProjects, lens: canLens, mi: canMi,
  }), [canCatalog, canDev, canQc, canSales, canProjects, canLens, canMi]);

  const portals = useMemo(() => buildPortals(access), [access]);
  const pages = useMemo(() => buildPages(access), [access]);
  const lenses = useMemo(() => (!queryEnabled || !canLens || lensQuery.isFetching)
    ? [] : (lensQuery.data || []).filter((row) => lensMatch(row, term)),
  [queryEnabled, canLens, lensQuery.isFetching, lensQuery.data, term]);
  const ltds = useMemo(() => (!queryEnabled || !canLens || ltdQuery.isFetching) ? []
    : buildLtd(ltdQuery.data || []).filter((row) => [row.code, row.name, row.lens_no]
      .some((value) => String(value || '').toLowerCase().includes(term))),
  [queryEnabled, canLens, ltdQuery.isFetching, ltdQuery.data, term]);

  return {
    items: server.items, devs: server.devs, designers: server.designers, brands: server.brands,
    portals, pages, lenses, ltds, orders: server.orders, acetates: server.acetates,
    serverMatched: true,
    totals: { item: server.itemsTotal, dev: server.devsTotal, order: server.ordersTotal, acetate: server.acetatesTotal },
    loading: enabled && (!permissionsReady || requestState.waiting || server.loading || (canLens && (lensQuery.isFetching || ltdQuery.isFetching))),
    error: permissionQuery.error || server.error || (canLens && (lensQuery.error || ltdQuery.error)) || null,
    accountId,
  };
}
